import React from "react";
import { Typography, Box } from "@mui/material";
import { marked } from "marked";
import { RendererType, ListType } from "./DataTable";

// Configure marked to open links in new tabs
marked.use({
  renderer: {
    link(token) {
      const href = token.href;
      const title = token.title || "";
      const text = token.text;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" title="${title}">${text}</a>`;
    },
  },
});

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const linkifyText = (text: string): string => {
  if (!text) return text;
  const urlRegex = /(https?:\/\/[^\s\)]+)/g;
  return text.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline; cursor: pointer;">${url}</a>`;
  });
};

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || isNaN(Number(amount))) {
    return "$0.00";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(amount));
};

const formatPercentage = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || isNaN(Number(amount))) {
    return "0.00%";
  }
  return Number(amount).toFixed(2) + "%";
};

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return "0";
  }
  return new Intl.NumberFormat("en-US").format(Number(value));
};

// ============================================================================
// BASE CELL WRAPPER - Single source of truth for cell styling
// ============================================================================

interface BaseCellProps {
  children: React.ReactNode;
  isLink?: boolean;
  onClick?: () => void;
}

const BaseCell: React.FC<BaseCellProps> = ({
  children,
  isLink = false,
  onClick,
}) => {
  return (
    <Typography
      variant="caption"
      component={isLink ? "a" : "span"}
      onClick={onClick}
      sx={{
        fontWeight: isLink ? 600 : 400,
        color: isLink ? "#3b82f6" : "#64748b",
        fontSize: "0.7rem",
        wordWrap: "break-word",
        whiteSpace: "normal",
        lineHeight: 1.4,
        padding: 0,
        cursor: isLink || onClick ? "pointer" : "default",
        textDecoration: "none",
        "&:hover":
          isLink || onClick
            ? {
                color: "#2563eb",
              }
            : {},
      }}
    >
      {children}
    </Typography>
  );
};

// ============================================================================
// RENDERING LOGIC - Pure functions that transform data
// ============================================================================

interface CellRendererProps {
  value: any;
  columnName: string;
  onClick?: () => void;
  rendererType?: RendererType;
  listType?: ListType;
}

// Render URL with external link
const renderUrl = (value: string) => {
  const isAvailable = value && value !== "NOT AVAILABLE" && value.trim() !== "";

  if (!isAvailable) {
    return <BaseCell>{value || "N/A"}</BaseCell>;
  }

  return (
    <Typography
      variant="caption"
      component="a"
      href={value}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        fontWeight: 400,
        color: "#3b82f6",
        fontSize: "0.7rem",
        textDecoration: "underline",
        cursor: "pointer",
        wordWrap: "break-word",
        whiteSpace: "normal",
        lineHeight: 1.4,
        padding: 0,
        "&:hover": { color: "#2563eb" },
      }}
    >
      {value}
    </Typography>
  );
};

// Render currency
const renderCurrency = (value: number) => {
  return <BaseCell>{formatCurrency(value)}</BaseCell>;
};

// Render percentage
const renderPercentage = (value: number) => {
  return <BaseCell>{formatPercentage(value)}</BaseCell>;
};

// Render number with thousand separators
const renderNumber = (value: number) => {
  return <BaseCell>{formatNumber(value)}</BaseCell>;
};

// Render list (ordered or unordered)
export const renderList = (value: string, listType: "ol" | "ul" = "ol") => {
  if (!value) return null;

  const ListTag = listType;

  return (
    <BaseCell>
      <ListTag style={{ margin: 0, padding: "1rem" }}>
        {value.split("\n").map((item: string, index: number) => (
          <li key={index} style={{ marginBottom: "0.35rem" }}>
            {item}
          </li>
        ))}
      </ListTag>
    </BaseCell>
  );
};

// Render markdown content
export const renderMarkdown = (value: string) => {
  if (!value) return null;

  return (
    <Box
      dangerouslySetInnerHTML={{
        __html: marked.parse(value, { async: false }) as string,
      }}
      sx={{
        wordWrap: "break-word",
        whiteSpace: "normal",
        fontSize: "0.7rem",
        lineHeight: 1.4,
        "& p": { margin: 0, marginBottom: "0.25rem" },
        "& p:last-child": { marginBottom: 0 },
        "& ul, & ol": {
          margin: 0,
          paddingLeft: "1rem",
          marginBottom: "0.25rem",
        },
        "& ul:last-child, & ol:last-child": { marginBottom: 0 },
        "& li": { marginBottom: "0.1rem" },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          margin: 0,
          marginBottom: "0.25rem",
          fontSize: "0.75rem",
          fontWeight: 600,
        },
        "& strong": { fontWeight: 600 },
        "& a": {
          color: "#3b82f6",
          textDecoration: "underline",
          cursor: "pointer",
          "&:hover": { color: "#2563eb" },
        },
      }}
    />
  );
};

// Render text with linkified URLs
const renderLinkifiedText = (value: string) => {
  if (!value) return null;

  return (
    <BaseCell>
      <span dangerouslySetInnerHTML={{ __html: linkifyText(value) }} />
    </BaseCell>
  );
};

// ============================================================================
// MAIN CELL RENDERER - Single entry point with clear routing logic
// ============================================================================

export const TableCellRenderer: React.FC<CellRendererProps> = ({
  value,
  columnName,
  onClick,
  rendererType,
  listType = ListType.UNORDERED,
}) => {
  // Route based on rendererType
  switch (rendererType) {
    case RendererType.URL:
      return renderUrl(value);

    case RendererType.CURRENCY:
      return renderCurrency(Number(value));

    case RendererType.NUMBER:
      return renderNumber(Number(value));

    case RendererType.LIST:
      return renderList(value, listType);

    case RendererType.MARKDOWN:
      return renderMarkdown(value);

    case RendererType.PERCENTAGE:
      return renderPercentage(value);

    case RendererType.LINKIFIED_TEXT:
      return renderLinkifiedText(value);

    case RendererType.TEXT:
    default:
      if (onClick) {
        return (
          <BaseCell isLink onClick={onClick}>
            {value}
          </BaseCell>
        );
      }

      // Default - plain text
      return <BaseCell>{value}</BaseCell>;
  }
};
